import React, { useState, useRef } from 'react';
import { Plus, Trash2, FileText } from 'lucide-react';
import { authAxios } from '@/lib/api';

const INPUT_CLS = 'w-full border border-earth/15 rounded-md px-3 py-2.5 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-saffron/30 bg-cream/30';
const LABEL_CLS = 'text-[10px] uppercase tracking-wider text-earth/50 font-sans font-semibold mb-1 block';

function AddCertificateForm({ onAdded }) {
  const [name, setName] = useState('');
  const [number, setNumber] = useState('');
  const fileRef = useRef(null);

  const submit = async () => {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('number', number);
    if (fileRef.current?.files[0]) formData.append('file', fileRef.current.files[0]);
    await authAxios.post('/api/certificates', formData);
    setName('');
    setNumber('');
    if (fileRef.current) fileRef.current.value = '';
    onAdded();
  };

  return (
    <div className="bg-white rounded-lg border border-saffron-100/50 shadow-card-lift p-6" data-testid="add-cert-form">
      <h3 className="font-display text-base font-bold text-earth-dark mb-4">Add Certificate / Document</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className={LABEL_CLS}>Certificate Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. FSSAI License" className={INPUT_CLS} data-testid="cert-name" />
        </div>
        <div>
          <label className={LABEL_CLS}>License/Registration Number</label>
          <input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="Number" className={INPUT_CLS} data-testid="cert-number" />
        </div>
        <div>
          <label className={LABEL_CLS}>PDF Document</label>
          <input ref={fileRef} type="file" accept=".pdf" className="w-full border border-earth/15 rounded-md px-3 py-2 text-sm font-sans bg-cream/30 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-saffron/10 file:text-saffron file:font-semibold" data-testid="cert-file" />
        </div>
      </div>
      <button onClick={submit} disabled={!name} className="mt-4 bg-saffron text-white font-sans font-semibold text-xs px-5 py-2.5 rounded-md hover:bg-saffron-dark transition-colors disabled:opacity-40 flex items-center gap-1.5" data-testid="cert-save">
        <Plus size={12} /> Add Certificate
      </button>
    </div>
  );
}

function CertificateCard({ cert, onDelete }) {
  return (
    <div className="bg-white rounded-lg border border-saffron-100/50 shadow-card-lift p-5" data-testid={`cert-${cert.id}`}>
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-lg bg-saffron/10 flex items-center justify-center flex-shrink-0">
          <FileText size={18} className="text-saffron" />
        </div>
        <button onClick={onDelete} className="text-chilli/30 hover:text-chilli transition-colors" data-testid={`delete-cert-${cert.id}`}><Trash2 size={14} /></button>
      </div>
      <h4 className="font-display text-sm font-bold text-earth-dark mt-3">{cert.name}</h4>
      {cert.number && <p className="text-xs text-saffron font-sans font-medium mt-1">{cert.number}</p>}
      {cert.file_url && (
        <a href={cert.file_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] text-saffron/70 font-sans mt-2 hover:text-saffron transition-colors" data-testid={`view-cert-${cert.id}`}>
          <FileText size={10} /> View PDF
        </a>
      )}
    </div>
  );
}

export default function CertificatesTab({ certificates, onRefresh }) {
  const handleDelete = async (id) => {
    await authAxios.delete(`/api/certificates/${id}`);
    onRefresh();
  };

  return (
    <div className="space-y-6" data-testid="certificates-tab">
      <AddCertificateForm onAdded={onRefresh} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {certificates.map(cert => (
          <CertificateCard key={cert.id} cert={cert} onDelete={() => handleDelete(cert.id)} />
        ))}
      </div>
    </div>
  );
}
